import { useEffect, useMemo, useRef, useState } from 'react';
import { faCalendar } from '@fortawesome/pro-solid-svg-icons/faCalendar';
import { faPenToSquare } from '@fortawesome/pro-solid-svg-icons/faPenToSquare';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Anchor, Badge, Checkbox, Group, Stack, Switch, Textarea, TextInput } from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import { DateTime } from 'luxon';
import { FormattedMessage, useIntl } from 'react-intl';

import { shipFromStepMessages } from './ship-from-step-messages';
import { formatTimeDisplay } from './ship-from-step-utils';
import styles from './ShipFromStep.module.css';
import { applyLtlDefaultsToFormState, areLtlDefaultsDirty, hasCompleteLtlPickupWindow } from '../../../../ltl/ltl-defaults';
import { getTimeZoneAbbreviation } from '../../../../utils/datetime';
import { Dropdown } from '../../../common/Dropdown';
import { MAX_SHIP_DATE_DAYS_AHEAD } from '../../wizard-step-validation';
import { PICKUP_SERVICE_FIELDS } from '../services-grid/pickup-service-fields';
import { ServicesGrid } from '../services-grid/ServicesGrid';

import type { LtlDefaults } from '../../../../types/ship-from';
import type { ShipFromOption } from '../../../freight-browser/freight-browser-types';
import type { FreightBookingFormState } from '../../freight-booking-wizard-types';
import type { MessageDescriptor } from 'react-intl';

export type ShipFromStepProps = {
  formState: FreightBookingFormState;
  onFormChange: (next: FreightBookingFormState) => void;
  shipFromOptions: ShipFromOption[];
  onAddShipFrom?: () => void;
  /** Formatted inline error messages keyed by field path (e.g. `'selectedShipFromId'`). */
  fieldErrors?: Record<string, string>;
  /**
   * Called when the user clicks "Edit ship from". When absent the link is not rendered.
   * The container opens ShipFromModal with the current selectedShipFromId.
   */
  onEditShipFrom?: () => void;
  /** True while the save-back API call is in flight. Disables the save-defaults checkbox. */
  isSavingLtlDefaults?: boolean;
  /** Permission gate — hide the save-defaults checkbox when the user lacks the Configuration role. */
  canSaveLtlDefaults?: boolean;
  /**
   * True when the selected location already has saved LTL defaults.
   * Drives the "Save as default" vs "Update default" label switch.
   */
  hasSavedLtlDefaults?: boolean;
};

type PickupTimeLabelProps = {
  label: string;
  tzAbbreviation: string;
};

/**
 * Renders the asterisk inline instead of via `withAsterisk` so the timezone
 * abbreviation can sit after it — Mantine always appends its own asterisk last.
 */
const PickupTimeLabel = ({ label, tzAbbreviation }: PickupTimeLabelProps) => (
  <>
    {label}
    <span aria-hidden className={styles.requiredAsterisk}>
      {' '}
      *
    </span>
    {tzAbbreviation && <span className={styles.labelTimeZone}>{tzAbbreviation}</span>}
  </>
);

export const ShipFromStep = ({
  formState,
  onFormChange,
  shipFromOptions,
  onAddShipFrom,
  fieldErrors,
  onEditShipFrom,
  isSavingLtlDefaults,
  canSaveLtlDefaults,
  hasSavedLtlDefaults,
}: ShipFromStepProps) => {
  const intl = useIntl();

  const selectData = shipFromOptions.map((option) => ({ value: option.id, label: option.name }));

  const selectedOption = shipFromOptions.find((o) => o.id === formState.selectedShipFromId);
  const tzAbbreviation = getTimeZoneAbbreviation(selectedOption?.address?.ianaTimeZone, formState.shipmentDate);

  const handleShipFromChange = (value: string | null) => {
    const nextId = value ?? '';
    const option = shipFromOptions.find((o) => o.id === nextId);
    // Single onFormChange call — applyLtlDefaultsToFormState handles prefill and
    // AC6 normalization. Two calls would trigger two re-quotes via extractRequoteSnapshot.
    onFormChange(applyLtlDefaultsToFormState(formState, nextId, option?.ltlDefaults));
  };

  const savedDefaults = selectedOption?.ltlDefaults ?? null;
  const isDirty = areLtlDefaultsDirty(formState, savedDefaults);
  const showSaveCheckbox = !!formState.selectedShipFromId && canSaveLtlDefaults;
  const isSaveDefaultsAllowed = isDirty && hasCompleteLtlPickupWindow(formState);

  // Every write funnels through here so the save-back intent can never outlive the
  // conditions that permitted it. Without this the flag survives a revert while the
  // checkbox sits disabled, then silently re-checks itself on the next unrelated edit
  // — and the container would save defaults the user believes they unchecked.
  const handleFormChange = (next: FreightBookingFormState) => {
    const isStillAllowed = areLtlDefaultsDirty(next, savedDefaults) && hasCompleteLtlPickupWindow(next);
    onFormChange(next.shouldSaveLtlDefaults && !isStillAllowed ? { ...next, shouldSaveLtlDefaults: false } : next);
  };

  // Track whether the user has explicitly opened the instructions textarea.
  // Reset on location change so switching to a location with no saved instructions
  // hides the textarea again. The OR with !!formState.pickupInstructions ensures
  // the textarea is shown immediately when a location change prefills instructions.
  const [instructionsOpenedByUser, setInstructionsOpenedByUser] = useState(!!formState.pickupInstructions);
  // Holds the text while the switch is off so the toggle is non-destructive.
  const hiddenInstructionsRef = useRef('');
  useEffect(() => {
    setInstructionsOpenedByUser(!!formState.pickupInstructions);
    hiddenInstructionsRef.current = '';
    // Only re-sync when the selected location changes, not on every formState mutation.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formState.selectedShipFromId]);
  const showPickupInstructions = instructionsOpenedByUser || !!formState.pickupInstructions;

  const handleShipDateChange = (value: string | null) => onFormChange({ ...formState, shipmentDate: value ?? '' });

  const today = useMemo(() => DateTime.now().startOf('day').toJSDate(), []);
  const maxShipDate = useMemo(() => DateTime.now().startOf('day').plus({ days: MAX_SHIP_DATE_DAYS_AHEAD }).toJSDate(), []);

  const handleTradeshowPickupField = (field: keyof FreightBookingFormState['tradeshowPickup'], value: string) =>
    onFormChange({ ...formState, tradeshowPickup: { ...formState.tradeshowPickup, [field]: value } });

  return (
    <Stack gap='md'>
      <div className={styles.topRow}>
        <DatePickerInput
          data-testid='ship-date-input'
          error={fieldErrors?.['shipmentDate']}
          label={intl.formatMessage(shipFromStepMessages.shipDateLabel)}
          maxDate={maxShipDate}
          minDate={today}
          onChange={handleShipDateChange}
          placeholder={intl.formatMessage(shipFromStepMessages.shipDatePlaceholder)}
          rightSection={<FontAwesomeIcon icon={faCalendar} size='sm' />}
          value={formState.shipmentDate ? DateTime.fromISO(formState.shipmentDate).toJSDate() : null}
          withAsterisk
        />
        <div>
          <Dropdown
            data={selectData}
            data-testid='ship-from-select'
            error={fieldErrors?.['selectedShipFromId']}
            label={
              <Group justify='space-between' wrap='nowrap'>
                <span>
                  <FormattedMessage {...shipFromStepMessages.selectShipFromLabel} />
                  <span aria-hidden className={styles.requiredAsterisk}>
                    {' '}
                    *
                  </span>
                </span>
                <Anchor className={styles.addLink} component='button' onClick={onAddShipFrom} size='sm' type='button'>
                  <FormattedMessage {...shipFromStepMessages.addShipFrom} />
                </Anchor>
              </Group>
            }
            onChange={handleShipFromChange}
            placeholder={intl.formatMessage(shipFromStepMessages.shipFromPlaceholder)}
            value={formState.selectedShipFromId || null}
          />
          {onEditShipFrom && formState.selectedShipFromId && (
            <Anchor
              className={styles.editShipFromLink}
              component='button'
              data-testid='edit-ship-from-link'
              onClick={onEditShipFrom}
              size='sm'
              type='button'
            >
              <FontAwesomeIcon icon={faPenToSquare} /> <FormattedMessage {...shipFromStepMessages.editShipFrom} />
            </Anchor>
          )}
        </div>
      </div>

      <Group grow>
        <TextInput
          data-testid='pickup-ready-time'
          disabled={formState.willArrangePickupSeparately}
          error={fieldErrors?.['pickupReadyTime']}
          label={<PickupTimeLabel label={intl.formatMessage(shipFromStepMessages.pickupReadyTimeLabel)} tzAbbreviation={tzAbbreviation} />}
          onChange={(event) => handleFormChange({ ...formState, pickupReadyTime: event.currentTarget.value })}
          type='time'
          value={formState.pickupReadyTime}
        />
        <TextInput
          data-testid='pickup-close-time'
          disabled={formState.willArrangePickupSeparately}
          error={fieldErrors?.['pickupCloseTime']}
          label={<PickupTimeLabel label={intl.formatMessage(shipFromStepMessages.pickupCloseTimeLabel)} tzAbbreviation={tzAbbreviation} />}
          onChange={(event) => handleFormChange({ ...formState, pickupCloseTime: event.currentTarget.value })}
          type='time'
          value={formState.pickupCloseTime}
        />
      </Group>

      <Checkbox
        checked={formState.willArrangePickupSeparately}
        data-testid='arrange-pickup-separately'
        label={intl.formatMessage(shipFromStepMessages.arrangePickupSeparately)}
        onChange={(event) =>
          handleFormChange({
            ...formState,
            willArrangePickupSeparately: event.currentTarget.checked,
            ...(event.currentTarget.checked ? { pickupReadyTime: '', pickupCloseTime: '' } : {}),
          })
        }
      />

      <Stack gap='sm'>
        <span className={styles.servicesHeader}>
          <FormattedMessage {...shipFromStepMessages.pickupServicesHeader} />
        </span>
        <ServicesGrid
          fields={PICKUP_SERVICE_FIELDS}
          onChange={(next) => handleFormChange({ ...formState, pickupServices: next })}
          value={formState.pickupServices}
        />
      </Stack>

      <Switch
        checked={showPickupInstructions}
        classNames={{ label: styles.switchLabel }}
        data-testid='pickup-instructions-toggle'
        label={intl.formatMessage(shipFromStepMessages.pickupInstructionsLabel)}
        onChange={(event) => {
          const isOn = event.currentTarget.checked;
          setInstructionsOpenedByUser(isOn);
          // Off must clear the field — a hidden value would still ship in the save
          // payload — but the text is stashed so toggling back on restores it.
          if (isOn) {
            handleFormChange({ ...formState, pickupInstructions: hiddenInstructionsRef.current });
          } else {
            hiddenInstructionsRef.current = formState.pickupInstructions;
            handleFormChange({ ...formState, pickupInstructions: '' });
          }
        }}
      />
      {showPickupInstructions && (
        <Textarea
          data-testid='pickup-instructions'
          label={intl.formatMessage(shipFromStepMessages.pickupInstructionsLabel)}
          onChange={(event) => handleFormChange({ ...formState, pickupInstructions: event.currentTarget.value })}
          value={formState.pickupInstructions}
        />
      )}

      {formState.pickupServices.isTradeshowPickup && (
        <Group grow>
          <TextInput
            data-testid='tradeshow-pickup-name'
            label={intl.formatMessage(shipFromStepMessages.tradeshowNameLabel)}
            onChange={(event) => handleTradeshowPickupField('name', event.currentTarget.value)}
            value={formState.tradeshowPickup.name}
          />
          <TextInput
            data-testid='tradeshow-pickup-booth'
            label={intl.formatMessage(shipFromStepMessages.boothNumberLabel)}
            onChange={(event) => handleTradeshowPickupField('boothNumber', event.currentTarget.value)}
            value={formState.tradeshowPickup.boothNumber}
          />
        </Group>
      )}

      {showSaveCheckbox && (
        <Checkbox
          checked={formState.shouldSaveLtlDefaults}
          classNames={{ description: styles.saveDefaultsDescription }}
          data-testid='save-ltl-defaults'
          description={intl.formatMessage(shipFromStepMessages.ltlDefaultsHelperText)}
          disabled={!isSaveDefaultsAllowed || isSavingLtlDefaults}
          label={
            hasSavedLtlDefaults
              ? intl.formatMessage(shipFromStepMessages.updateDefaultSelections)
              : intl.formatMessage(shipFromStepMessages.saveAsDefaultSelections)
          }
          onChange={(event) => handleFormChange({ ...formState, shouldSaveLtlDefaults: event.currentTarget.checked })}
        />
      )}
    </Stack>
  );
};

const formatShipDate = (isoDate: string): string => {
  if (!isoDate) return '';
  const parsed = DateTime.fromISO(isoDate);
  return parsed.isValid ? parsed.toLocaleString(DateTime.DATE_FULL) : isoDate;
};

const formatShipFromAddress = (address: ShipFromOption['address']): string => {
  if (!address) return '';
  const stateZip = [address.state, address.postalCode].filter(Boolean).join(' ');
  const cityStateZip = [address.city, stateZip].filter(Boolean).join(', ');
  return [address.line1, address.line2, cityStateZip, address.countryCode].filter(Boolean).join(', ');
};

const PICKUP_FIELD_BY_KEY: Record<keyof LtlDefaults['pickupServices'], MessageDescriptor> = PICKUP_SERVICE_FIELDS.reduce(
  (accumulator, field) => ({ ...accumulator, [field.key]: field.label }),
  {} as Record<keyof LtlDefaults['pickupServices'], MessageDescriptor>,
);

export type ShipFromStepSummaryProps = {
  formState: FreightBookingFormState;
  shipFromOptions: ShipFromOption[];
  pickupReadyTime?: string;
  pickupCloseTime?: string;
};

export const ShipFromStepSummary = ({ formState, shipFromOptions, pickupReadyTime, pickupCloseTime }: ShipFromStepSummaryProps) => {
  const intl = useIntl();

  const selectedShipFrom = shipFromOptions.find((option) => option.id === formState.selectedShipFromId);
  const shipDate = formatShipDate(formState.shipmentDate);
  const address = formatShipFromAddress(selectedShipFrom?.address);

  const tzAbbreviation = getTimeZoneAbbreviation(selectedShipFrom?.address?.ianaTimeZone, formState.shipmentDate);

  const hasPickupWindow = !!(pickupReadyTime || pickupCloseTime);
  const pickupWindowDisplay = hasPickupWindow
    ? intl.formatMessage(shipFromStepMessages.summaryPickupWindow, {
        from: formatTimeDisplay(pickupReadyTime ?? ''),
        to: formatTimeDisplay(pickupCloseTime ?? ''),
        tz: tzAbbreviation ? ` ${tzAbbreviation}` : '',
      })
    : '';

  const activeServices = PICKUP_SERVICE_FIELDS.filter((field) => formState.pickupServices[field.key]).map((field) =>
    intl.formatMessage(PICKUP_FIELD_BY_KEY[field.key]),
  );

  const hasTradeshow =
    formState.pickupServices.isTradeshowPickup && (formState.tradeshowPickup.name || formState.tradeshowPickup.boothNumber);

  const tradeshowParts = [
    formState.tradeshowPickup.name
      ? intl.formatMessage(shipFromStepMessages.summaryTradeshowName, { name: formState.tradeshowPickup.name })
      : '',
    formState.tradeshowPickup.boothNumber
      ? intl.formatMessage(shipFromStepMessages.summaryBoothNumber, { booth: formState.tradeshowPickup.boothNumber })
      : '',
  ].filter(Boolean);

  return (
    <div className={styles.summary}>
      {selectedShipFrom && (
        <div className={styles.summarySection}>
          <span className={styles.summaryLabel}>
            <FormattedMessage {...shipFromStepMessages.summaryLocationLabel} />
          </span>
          <span className={styles.summaryValue}>{selectedShipFrom.name}</span>
          {address && <span className={styles.summarySecondary}>{address}</span>}
        </div>
      )}
      {shipDate && (
        <div className={styles.summarySection}>
          <span className={styles.summaryLabel}>
            <FormattedMessage {...shipFromStepMessages.shipDateLabel} />
          </span>
          <span className={styles.summaryValue}>{shipDate}</span>
        </div>
      )}
      {hasPickupWindow && (
        <div className={styles.summarySection}>
          <span className={styles.summaryLabel}>
            <FormattedMessage {...shipFromStepMessages.pickupWindowLabel} />
          </span>
          <span className={styles.summaryValue}>{pickupWindowDisplay}</span>
        </div>
      )}
      {activeServices.length > 0 && (
        <div className={styles.summarySection}>
          <span className={styles.summaryLabel}>
            <FormattedMessage {...shipFromStepMessages.pickupServicesHeader} />
          </span>
          <div className={styles.tags}>
            {activeServices.map((service) => (
              <Badge color='light' key={service} size='lg' variant='filled'>
                {service}
              </Badge>
            ))}
          </div>
          {hasTradeshow && <span className={styles.summarySecondary}>{tradeshowParts.join(' • ')}</span>}
        </div>
      )}
    </div>
  );
};
